import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";

interface DealFinancingTabProps {
  kpis: any;
  onKpisChange: (data: any) => void;
}

export function DealFinancingTab({
  kpis,
  onKpisChange,
}: DealFinancingTabProps) {
  const handleLTCChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      financing: {
        ...(kpis.financing || {}),
        debtToEquityRatio: value,
        ltcPct: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleInterestRateChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      financing: {
        ...(kpis.financing || {}),
        interestRate: value,
        interestRatePct: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleAmortizationChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      financing: {
        ...(kpis.financing || {}),
        loanTermYears: value,
        amortYears: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleTaxRateChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      financing: {
        ...(kpis.financing || {}),
        taxRateOnEBT: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const getLTCValue = () => {
    return kpis.financing?.debtToEquityRatio || kpis.financing?.ltcPct || 60;
  };

  const getInterestRate = () => {
    return kpis.financing?.interestRate || kpis.financing?.interestRatePct || 5;
  };

  const getLoanTerm = () => {
    return kpis.financing?.loanTermYears || kpis.financing?.amortYears || 20;
  };

  const getAmortization = () => {
    return kpis.financing?.amortYears || 10;
  };

  const getTaxRate = () => {
    return kpis.financing?.taxRateOnEBT || 30;
  };

  const getDSCR = () => {
    return kpis.financing?.dscrTarget || 1.25;
  };

  const getTotalInvestment = () => {
    return kpis.investmentBudget?.grandTotal || 0;
  };

  return (
    <TabsContent value="financing" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Financing Structure</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="font-medium">LTC Ratio (Loan-to-Cost) %</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                className="w-24"
                value={getLTCValue()}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleLTCChange(value);
                }}
              />
              <div className="flex flex-wrap gap-2">
                {[0, 20, 30, 40, 50, 60, 70].map((v) => (
                  <Button
                    key={v}
                    variant={getLTCValue() === v ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-3 text-sm ${
                      getLTCValue() === v
                        ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                        : ""
                    }`}
                    onClick={() => handleLTCChange(v)}
                  >
                    {v}%
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Loan Terms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="font-medium">Interest Rate (%)</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                step="0.5"
                className="w-24"
                value={getInterestRate()}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleInterestRateChange(value);
                }}
              />
              <div className="flex flex-wrap gap-2">
                {[4.5, 5, 5.5, 6, 6.5, 7, 7.5].map((v) => (
                  <Button
                    key={v}
                    variant={getInterestRate() === v ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-3 text-sm ${
                      getInterestRate() === v
                        ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                        : ""
                    }`}
                    onClick={() => handleInterestRateChange(v)}
                  >
                    {v}%
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="font-medium">Loan Term (Years)</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                className="w-24"
                value={getLoanTerm()}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleAmortizationChange(value);
                }}
              />
              <div className="flex flex-wrap gap-2">
                {[10, 15, 20, 25].map((v) => (
                  <Button
                    key={v}
                    variant={getLoanTerm() === v ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-3 text-sm ${
                      getLoanTerm() === v
                        ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                        : ""
                    }`}
                    onClick={() => handleAmortizationChange(v)}
                  >
                    {v}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="font-medium">Amortization Period (Years)</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                className="w-24"
                value={getAmortization()}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  const updatedKpis = {
                    ...kpis,
                    financing: {
                      ...(kpis.financing || {}),
                      amortYears: value,
                    },
                  };
                  onKpisChange(updatedKpis);
                }}
              />
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15, 20].map((v) => (
                  <Button
                    key={v}
                    variant={getAmortization() === v ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-3 text-sm ${
                      getAmortization() === v
                        ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                        : ""
                    }`}
                    onClick={() => {
                      const updatedKpis = {
                        ...kpis,
                        financing: {
                          ...(kpis.financing || {}),
                          amortYears: v,
                        },
                      };
                      onKpisChange(updatedKpis);
                    }}
                  >
                    {v}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="font-medium">DSCR Target</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                step="0.01"
                className="w-24"
                value={getDSCR()}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  const updatedKpis = {
                    ...kpis,
                    financing: {
                      ...(kpis.financing || {}),
                      dscrTarget: value,
                    },
                  };
                  onKpisChange(updatedKpis);
                }}
              />
              <div className="flex flex-wrap gap-2">
                {[1.15, 1.2, 1.25, 1.3, 1.35].map((v) => (
                  <Button
                    key={v}
                    variant={getDSCR() === v ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-3 text-sm ${
                      getDSCR() === v
                        ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                        : ""
                    }`}
                    onClick={() => {
                      const updatedKpis = {
                        ...kpis,
                        financing: {
                          ...(kpis.financing || {}),
                          dscrTarget: v,
                        },
                      };
                      onKpisChange(updatedKpis);
                    }}
                  >
                    {v}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tax Assumptions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label className="font-medium">Tax Rate on EBT (%)</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                className="w-24"
                value={getTaxRate()}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  handleTaxRateChange(value);
                }}
              />
              <div className="flex flex-wrap gap-2">
                {[20, 25, 30, 35].map((v) => (
                  <Button
                    key={v}
                    variant={getTaxRate() === v ? "default" : "outline"}
                    size="sm"
                    className={`h-8 px-3 text-sm ${
                      getTaxRate() === v
                        ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                        : ""
                    }`}
                    onClick={() => handleTaxRateChange(v)}
                  >
                    {v}%
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Loan Calculation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span>Total Investment:</span>
              <span className="font-medium">
                {kpis.currency || "EUR"} {getTotalInvestment().toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>LTC Ratio:</span>
              <span className="font-medium">{getLTCValue()}%</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span>Loan Amount:</span>
              <span className="font-bold">
                {kpis.currency || "EUR"}{" "}
                {(
                  getTotalInvestment() *
                  (getLTCValue() / 100)
                ).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Equity Required:</span>
              <span className="font-bold">
                {kpis.currency || "EUR"}{" "}
                {(
                  getTotalInvestment() *
                  (1 - getLTCValue() / 100)
                ).toLocaleString()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}
